import { useEffect, useRef, useCallback, useState } from 'react';

const useWebSocket = (token) => {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const listenersRef = useRef(new Set());
  const reconnectTimeoutRef = useRef(null);
  const mountedRef = useRef(true);

  const addListener = useCallback((callback) => {
    listenersRef.current.add(callback);
    return () => listenersRef.current.delete(callback);
  }, []);

  const connect = useCallback(() => {
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;

    // Build WS URL from the backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    let wsUrl;
    if (backendUrl.startsWith('https://')) {
      wsUrl = `wss://${backendUrl.replace('https://', '')}/api/ws/${token}`;
    } else if (backendUrl.startsWith('http://')) {
      wsUrl = `ws://${backendUrl.replace('http://', '')}/api/ws/${token}`;
    } else {
      wsUrl = `wss://${window.location.host}/api/ws/${token}`;
    }

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (mountedRef.current) setConnected(true);
        // Start ping interval
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
        ws._pingInterval = pingInterval;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'pong') return; // Ignore pong
          // Notify all listeners
          listenersRef.current.forEach(cb => {
            try { cb(data); } catch (e) { console.error('WS listener error:', e); }
          });
        } catch (e) {
          console.error('WS parse error:', e);
        }
      };

      ws.onclose = () => {
        if (mountedRef.current) setConnected(false);
        clearInterval(ws._pingInterval);
        // Reconnect after 3s
        if (mountedRef.current && token) {
          reconnectTimeoutRef.current = setTimeout(connect, 3000);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch (e) {
      console.error('WS connection error:', e);
    }
  }, [token]);

  const sendMessage = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current) {
        clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
      }
    };
  }, [connect]);

  return { connected, addListener, sendMessage };
};

export default useWebSocket;
